<script setup lang="ts">
import { z } from 'zod';
import { ref, watch, onMounted, nextTick } from 'vue';
import { UiForm, UiFormField, UiInputField, UiCheckbox } from '@shopexperts/shared';
import LeadStepHeader from '../leads/LeadStepHeader.vue';
import LeadStepActions from '../leads/LeadStepActions.vue';

interface Props {
  currentStep: number;
  totalSteps: number;
  userFirstName?: string;
  userLastName?: string;
  submitLabel?: string;
  isLoading?: boolean;
  caption?: string;
  heading?: string;
  subheading?: string;
  storeLabel?: string;
  storePlaceholder?: string;
  brandLabel?: string;
  brandPlaceholder?: string;
  allowNoStoreYet?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  userFirstName: undefined,
  userLastName: undefined,
  submitLabel: 'Submit',
  isLoading: false,
  caption: 'Project Details',
  heading: 'One last step',
  subheading: "Just a couple more details and we're done.",
  storeLabel: 'Shopify Store URL',
  storePlaceholder: 'yourstoreurl.com',
  brandLabel: 'Brand Name (optional)',
  brandPlaceholder: 'Your Shopify Brand',
  allowNoStoreYet: true,
});

const emit = defineEmits<{
  (e: 'next', data: ContactDetailsData): void;
  (e: 'back'): void;
  (e: 'fieldBlur', data: Partial<ContactDetailsData>): void;
}>();

export interface ContactDetailsData {
  storeUrl: string;
  noStoreYet?: boolean;
  brandName?: string;
  firstName: string;
  lastName: string;
}

const schema = z.object({
  storeUrl: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string().max(500, 'Store URL must be 500 characters or less')
  ),
  noStoreYet: z.preprocess(
    (val) => val === undefined || val === null ? false : val,
    z.boolean().optional()
  ),
  brandName: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string().max(255, 'Brand name must be 255 characters or less').optional()
  ),
  firstName: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'First name is required')
      .max(255, 'First name must be 255 characters or less')
  ),
  lastName: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'Last name is required')
      .max(255, 'Last name must be 255 characters or less')
  ),
}).refine(
  (data) => Boolean((props.allowNoStoreYet && data.noStoreYet) || (data.storeUrl && data.storeUrl.length > 0)),
  {
    message: 'Store URL is required',
    path: ['storeUrl'],
  }
);

const model = defineModel<ContactDetailsData>({
  default: () => ({
    storeUrl: '',
    noStoreYet: false,
    brandName: '',
    firstName: '',
    lastName: '',
  }),
});

// Pre-fill firstName and lastName from user data if available
onMounted(() => {
  if (props.userFirstName && !model.value.firstName) {
    model.value.firstName = props.userFirstName;
  }
  if (props.userLastName && !model.value.lastName) {
    model.value.lastName = props.userLastName;
  }
});

// Watch for prop changes to pre-fill (in case user data loads after mount)
watch([() => props.userFirstName, () => props.userLastName], ([firstName, lastName]) => {
  if (firstName && !model.value.firstName) {
    model.value.firstName = firstName;
  }
  if (lastName && !model.value.lastName) {
    model.value.lastName = lastName;
  }
}, { immediate: true });

// Generate a placeholder store URL when user doesn't have a store
function generatePlaceholderStoreUrl(): string {
  const randomNum = Math.floor(Math.random() * 900000) + 100000; // 6-digit random number
  return `new-store-${randomNum}.myshopify.com`;
}

// Store the generated placeholder so it stays consistent during the session
const noStorePlaceholder = ref<string>('');

// Track the user's actual store URL when they check "no store"
const userStoreUrlBeforeNoStore = ref<string>('');

// Watch noStoreYet to manage the storeUrl field and trigger draft update
watch(() => model.value.noStoreYet, async (noStore, wasNoStore) => {
  if (!props.allowNoStoreYet) {
    return;
  }

  if (noStore && !wasNoStore) {
    // User just checked "no store" - save their current value and set placeholder
    userStoreUrlBeforeNoStore.value = model.value.storeUrl || '';
    // Generate a new placeholder if we don't have one yet
    if (!noStorePlaceholder.value) {
      noStorePlaceholder.value = generatePlaceholderStoreUrl();
    }
    // Reassign entire model to ensure parent is updated via v-model
    model.value = { ...model.value, storeUrl: noStorePlaceholder.value };
    // Wait for reactivity to flush, then trigger draft update
    await nextTick();
    handleFieldBlur('storeUrl');
  } else if (!noStore && wasNoStore) {
    // User just unchecked "no store" - restore their previous value
    const restoredUrl = userStoreUrlBeforeNoStore.value;
    userStoreUrlBeforeNoStore.value = '';
    // Reassign entire model to ensure parent is updated via v-model
    model.value = { ...model.value, storeUrl: restoredUrl };
    // Wait for reactivity to flush, then trigger draft update
    await nextTick();
    handleFieldBlur('storeUrl');
  }
});

// Track if we've emitted to avoid duplicate calls on same values
const lastEmittedSnapshot = ref<string>('');

function handleFieldBlur(fieldName: keyof ContactDetailsData) {
  // Create a snapshot of current values to check if anything changed
  const currentSnapshot = JSON.stringify(model.value);
  
  // Only emit if values have changed since last emit
  if (currentSnapshot !== lastEmittedSnapshot.value) {
    lastEmittedSnapshot.value = currentSnapshot;
    console.log('[ContactDetailsStep] Field blur:', fieldName, 'emitting all values:', model.value);
    // Emit all current values so parent can update the entire draft
    emit('fieldBlur', { ...model.value });
  }
}

function onSubmit(values: ContactDetailsData) {
  emit('next', values);
}

function onBack() {
  emit('back');
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <LeadStepHeader
      :caption="caption"
      :heading="heading"
      :subheading="subheading"
      :current-step="currentStep"
      :total-steps="totalSteps"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </LeadStepHeader>

    <UiForm
      v-model="model"
      :schema="schema"
      @submit="onSubmit"
    >
      <div class="flex flex-col gap-5">
        <!-- Store URL (hidden when noStoreYet is checked) -->
        <div class="flex flex-col gap-2">
          <UiFormField
            v-if="!(allowNoStoreYet && model.noStoreYet)"
            name="storeUrl"
            :label="storeLabel"
            type="text"
          >
            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
              <UiInputField
                :id="id"
                name="storeUrl"
                :model-value="modelValue as string"
                @update:model-value="onUpdateModelValue"
                @validate="() => { onValidate(); handleFieldBlur('storeUrl'); }"
                :invalid="invalid"
                :error="error"
                :placeholder="storePlaceholder"
              />
            </template>
          </UiFormField>

          <!-- No store checkbox -->
          <UiFormField
            v-if="allowNoStoreYet"
            name="noStoreYet"
            type="checkbox"
          >
            <template #default="{ modelValue, onUpdateModelValue }">
              <UiCheckbox
                name="noStoreYet"
                :model-value="modelValue as boolean"
                @update:model-value="onUpdateModelValue"
                label="I don't have a Shopify store yet."
              />
            </template>
          </UiFormField>
        </div>

        <!-- Brand Name (optional) -->
        <UiFormField
          name="brandName"
          :label="brandLabel"
          type="text"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiInputField
              :id="id"
              name="brandName"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              @validate="() => { onValidate(); handleFieldBlur('brandName'); }"
              :invalid="invalid"
              :error="error"
              :placeholder="brandPlaceholder"
            />
          </template>
        </UiFormField>

        <!-- First Name / Last Name side by side -->
        <div class="grid grid-cols-2 gap-4">
          <!-- First Name -->
          <UiFormField
            name="firstName"
            label="First Name"
            type="text"
          >
            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
              <UiInputField
                :id="id"
                name="firstName"
                :model-value="modelValue as string"
                @update:model-value="onUpdateModelValue"
                @validate="() => { onValidate(); handleFieldBlur('firstName'); }"
                :invalid="invalid"
                :error="error"
                placeholder="John"
              />
            </template>
          </UiFormField>

          <!-- Last Name -->
          <UiFormField
            name="lastName"
            label="Last Name"
            type="text"
          >
            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
              <UiInputField
                :id="id"
                name="lastName"
                :model-value="modelValue as string"
                @update:model-value="onUpdateModelValue"
                @validate="() => { onValidate(); handleFieldBlur('lastName'); }"
                :invalid="invalid"
                :error="error"
                placeholder="Doe"
              />
            </template>
          </UiFormField>
        </div>

        <LeadStepActions
          :current-step="currentStep"
          :total-steps="totalSteps"
          :is-loading="isLoading"
          :next-label="props.submitLabel"
          @back="onBack"
        />
      </div>
    </UiForm>
  </div>
</template>
